import { useDateFnsLocaleFormat } from "@/dictionaries/hooks"
import { useInterval } from "@/hooks/useInterval"
import { service } from "@/services/trackings/service"
import millify from "millify"
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts"
import { parseIsoYear } from "../helpers"
import { StatRecord, StatsProps } from "../types"
import { useStatsTheme } from "../useStatsTheme"

const reloadTime = 30000

/**
 * StatsVisitByYears
 */
export const StatsVisitByYears: React.FC<StatsProps> = ({
  trackingId,
  interval,
  setIsLoading = () => true,
  live = false,
}) => {
  const { colors } = useStatsTheme()
  const format = useDateFnsLocaleFormat()
  const [data, setData] = React.useState<StatRecord[]>([])
  const updateData = async () => {
    setIsLoading(true)
    const params = interval.from
      ? {
          from: T.formatISO(T.startOfYear(interval.from)),
          to: T.formatISO(T.endOfYear(interval.to)),
        }
      : { to: T.formatISO(T.endOfYear(interval.to)) }
    const { error, data } = G.isNullable(trackingId)
      ? await service.byYear(params)
      : await service.trackings.byYear(trackingId, params)
    setIsLoading(false)
    if (error) return
    const start =
      interval.from ??
      pipe(
        D.keys(data.stats),
        A.map(parseIsoYear),
        A.sortBy(d => d),
        A.head
      ) ??
      new Date()
    const years = T.eachYearOfInterval({ start, end: interval.to })

    const populated = A.map<Date, StatRecord>(years, date => {
      const key = T.format(date, "yyyy")
      return { name: key, value: data.stats[key] ?? 0 }
    })
    setData(populated)
  }
  React.useEffect(() => {
    updateData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval.from, interval.to])
  useInterval(updateData, live ? reloadTime : null)

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted))" />
        <XAxis
          dataKey="name"
          stroke="hsl(var(--muted-foreground))"
          tick={{ fontSize: "0.75em", fill: "hsl(var(--muted-foreground))" }}
          tickFormatter={(value: string) => format(parseIsoYear(value), "yyyy")}
        />
        <YAxis
          dataKey="value"
          stroke="hsl(var(--muted-foreground))"
          width={40}
          tick={{ fontSize: "0.75em", fill: "hsl(var(--muted-foreground))" }}
          tickFormatter={(value: number) => millify(value)}
        />
        <Tooltip content={<StatsVisitByYearsTooltip />} cursor={{ fill: "hsl(var(--muted))" }} />
        <Bar dataKey="value" fill={colors[5]} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/**
 * StatsVisitByYearsTooltip
 */
const StatsVisitByYearsTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
  const format = useDateFnsLocaleFormat()
  const { colors } = useStatsTheme()
  if (!(active && payload && payload.length)) return null
  const {
    value,
    payload: { name },
  } = A.getUnsafe(payload, 0)
  const date = parseIsoYear(name)
  return (
    <div className="rounded-md border bg-popover px-3 py-1.5 text-[0.75em] text-popover-foreground shadow-md">
      <b style={{ color: colors[5] }}>{format(date, "yyyy")}: </b> <span>{`${value}`}</span>
    </div>
  )
}
